import {
  ACTION_STATUS_LABELS,
  INITIATIVE_PROGRESS_MODE_LABELS,
  INITIATIVE_PROGRESS_MODES,
  INITIATIVE_STATUS_LABELS,
  INITIATIVE_STATUSES,
  RISK_IMPACT_LABELS,
  RISK_LEVELS,
  RISK_PROBABILITY_LABELS,
  RISK_SEVERITY_LABELS,
  formatCurrency,
  formatDate,
  formatDecimal,
  initiativeActionsSchema,
  initiativeInputSchema,
  initiativeListSchema,
  initiativeOptionsSchema,
  initiativeSchema,
  type Initiative,
  type InitiativeActionRef,
  type InitiativeInput,
  type InitiativeProgressModeName,
  type InitiativeOptions,
  type InitiativeStatusName,
  type RiskLevelName,
  type RiskSeverityName,
  type TrafficLight,
} from '@fluxus/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  CalendarRange,
  Eye,
  FolderKanban,
  ListChecks,
  Lock,
  Pencil,
  Plus,
  Rocket,
  Search,
  ShieldAlert,
  Target,
  Trash2,
  UserRound,
  Wallet,
} from 'lucide-react';
import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { StatusBadge } from '@/components/status/StatusBadge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Dialog } from '@/components/ui/dialog';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { CollaborationPanel } from '@/features/collaboration/CollaborationPanel';
import { ProjectPicker, useCurrentProject } from '@/features/workspace/workspace';
import { ApiError, errorMessage } from '@/lib/api/errors';
import { useAuth, useCan, useCurrentUser } from '@/lib/auth/session';
import { cn } from '@/lib/cn';
import { apiFieldErrors, hasFieldErrors, zodFieldErrors, type FieldErrors } from '@/lib/forms';
import { moneyInput, moneyText } from '@/lib/money';
import { ProgressBar } from '@/components/progress/ProgressBar';
import { INITIATIVE_STATUS_LIGHT } from '@/features/initiatives/status';

const SEVERITY_LIGHT: Record<RiskSeverityName, TrafficLight> = {
  LOW: 'green',
  MODERATE: 'yellow',
  HIGH: 'red',
  CRITICAL: 'red',
};

const PAGE_SIZE = 30;

const percent = (v: number | null) => (v === null ? '—' : `${formatDecimal(v)}%`);
const period = (i: Pick<Initiative, 'startDate' | 'endDate'>) =>
  i.startDate || i.endDate
    ? `${i.startDate ? formatDate(i.startDate) : '…'} a ${i.endDate ? formatDate(i.endDate) : '…'}`
    : 'Sem datas';

/** Execução › Iniciativas (T-090, RF-31, PRD §11.18): lista com filtros, detalhe, cadastro e arquivo. */
export function IniciativasPage() {
  const { organizationId = '' } = useParams();
  const user = useCurrentUser();
  const { workspace, projects, project, select } = useCurrentProject(organizationId);

  return (
    <PageContainer className="max-w-5xl">
      <PageHeader
        title="Iniciativas estratégicas"
        description="Como a empresa pretende alcançar cada objetivo: quem lidera, com que equipe e orçamento, até quando e com quais riscos."
      />
      {workspace.isPending ? (
        <LoadingState className="mt-6" rows={4} label="Carregando o projeto" />
      ) : workspace.isError ? (
        <ErrorState
          className="mt-6"
          error={workspace.error}
          onRetry={() => void workspace.refetch()}
          retrying={workspace.isFetching}
        />
      ) : !project ? (
        <EmptyState
          className="mt-6 rounded-lg border border-dashed border-border py-12"
          icon={FolderKanban}
          title="Nenhum projeto BSC em andamento"
          description="As iniciativas pertencem a um projeto. Quando a empresa tiver um projeto em andamento, elas aparecem aqui."
          action={
            user.organization.isFluxus ? (
              <Button asChild variant="outline">
                <Link to={`/empresas/${organizationId}`}>Abrir um projeto na página da empresa</Link>
              </Button>
            ) : null
          }
        />
      ) : (
        <>
          <div className="mt-4">
            <ProjectPicker projects={projects} project={project} onSelect={select} />
          </div>
          <InitiativesSection
            key={project.id}
            organizationId={organizationId}
            projectId={project.id}
            readOnly={project.status !== 'ACTIVE'}
          />
        </>
      )}
    </PageContainer>
  );
}

function InitiativesSection({
  organizationId,
  projectId,
  readOnly,
}: {
  organizationId: string;
  projectId: string;
  /** Projeto arquivado: só consulta. */
  readOnly: boolean;
}) {
  const { client } = useAuth();
  const canDo = useCan();
  const can: typeof canDo = (module, action) => !readOnly && canDo(module, action);
  const queryClient = useQueryClient();
  const base = `/organizations/${organizationId}/projects/${projectId}/initiatives`;
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [objectiveId, setObjectiveId] = useState(() => params.get('objetivo') ?? '');
  const [leaderId, setLeaderId] = useState('');
  const [perspectiveId, setPerspectiveId] = useState('');
  const [status, setStatus] = useState<InitiativeStatusName | ''>('');
  const [archived, setArchived] = useState(false);
  // ?riscos=sem-tratamento abre já filtrado (link da Visão Geral — T-140b).
  const [untreatedOnly, setUntreatedOnly] = useState(() => params.get('riscos') === 'sem-tratamento');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Initiative | 'new' | null>(null);
  const [busy, setBusy] = useState(false);
  const [archiving, setArchiving] = useState<Initiative | null>(null);
  const detailId = params.get('iniciativa');
  const ids = {
    search: useId(),
    objective: useId(),
    leader: useId(),
    perspective: useId(),
    status: useId(),
  };

  useEffect(() => {
    const next = search.trim();
    if (next === q) return;
    const timer = setTimeout(() => {
      setQ(next);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, q]);

  const options = useQuery({
    queryKey: ['initiatives', projectId, 'options'],
    queryFn: ({ signal }) => client.request(`${base}/options`, { schema: initiativeOptionsSchema, signal }),
  });
  const filters = { q, objectiveId, leaderId, perspectiveId, status, archived, untreatedOnly, page };
  const list = useQuery({
    queryKey: ['initiatives', projectId, 'list', filters],
    queryFn: ({ signal }) =>
      client.request(base, {
        query: {
          q: q || undefined,
          objectiveId: objectiveId || undefined,
          leaderId: leaderId || undefined,
          perspectiveId: perspectiveId || undefined,
          status: status || undefined,
          archived: archived ? 'true' : undefined,
          untreatedRisks: untreatedOnly ? 'true' : undefined,
          page,
          pageSize: PAGE_SIZE,
        },
        schema: initiativeListSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });
  // Os planos mostram o nome da iniciativa de origem e a lista de iniciativas para filtrar: atualizam junto.
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['initiatives', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['action-plans', projectId] }),
    ]);

  const openDetail = (id: string | null) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set('iniciativa', id);
        else next.delete('iniciativa');
        return next;
      },
      { replace: true },
    );

  const toggleArchive = useMutation({
    meta: { handlesError: true },
    mutationFn: (i: Initiative) =>
      client.request(`${base}/${i.id}/${i.archivedAt ? 'restore' : 'archive'}`, {
        method: 'POST',
        schema: initiativeSchema,
      }),
    onSuccess: async (i) => {
      setArchiving(null);
      toast.success(i.archivedAt ? 'Iniciativa arquivada' : 'Iniciativa restaurada', { description: i.name });
      await refresh();
    },
    onError: (err) => {
      setArchiving(null);
      toast.error('Não foi possível concluir', { description: errorMessage(err) });
    },
  });

  async function save(input: InitiativeInput) {
    const target = editing === 'new' ? null : editing;
    const saved = await client.request(target ? `${base}/${target.id}` : base, {
      method: target ? 'PUT' : 'POST',
      body: input,
      schema: initiativeSchema,
    });
    setEditing(null);
    toast.success(target ? 'Iniciativa salva' : 'Iniciativa criada', { description: saved.name });
    await refresh();
  }

  const filtered = Boolean(q || objectiveId || leaderId || perspectiveId || status || untreatedOnly);
  const opts = options.data;
  const clearFilters = () => {
    setSearch('');
    setObjectiveId('');
    setLeaderId('');
    setPerspectiveId('');
    setStatus('');
    setUntreatedOnly(false);
    setPage(1);
  };
  const objectivesForFilter = perspectiveId
    ? opts?.objectives.filter((o) => o.perspectiveId === perspectiveId)
    : opts?.objectives;

  return (
    <div className="mt-6 flex flex-col gap-5">
      {readOnly ? (
        <p className="flex items-center gap-2 rounded-lg border border-border bg-muted px-4 py-3 text-sm">
          <Lock className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          Projeto arquivado: as iniciativas ficam disponíveis só para consulta.
        </p>
      ) : null}
      {options.isError ? (
        <FormAlert tone="error">
          <span className="flex flex-wrap items-center gap-2">
            Não foi possível carregar os objetivos e as pessoas (filtros e cadastro):{' '}
            {errorMessage(options.error)}
            <Button
              variant="outline"
              size="sm"
              loading={options.isFetching}
              onClick={() => void options.refetch()}
            >
              Tentar de novo
            </Button>
          </span>
        </FormAlert>
      ) : null}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <label htmlFor={ids.search} className="sr-only">
              Buscar iniciativa
            </label>
            <Search
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              id={ids.search}
              type="search"
              placeholder="Buscar pelo nome"
              value={search}
              maxLength={100}
              onChange={(e) => setSearch(e.target.value)}
              className="h-11 pl-9"
            />
          </div>
          {can('iniciativas', 'create') && !archived ? (
            <Button
              className="h-11"
              onClick={() => setEditing('new')}
              disabled={!opts || opts.objectives.length === 0}
            >
              <Plus aria-hidden />
              Nova iniciativa
            </Button>
          ) : null}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Filter
            id={ids.perspective}
            label="Perspectiva"
            value={perspectiveId}
            onChange={(v) => {
              setPerspectiveId(v);
              // O objetivo escolhido só fica se for da perspectiva.
              if (v && opts?.objectives.find((o) => o.id === objectiveId)?.perspectiveId !== v) {
                setObjectiveId('');
              }
              setPage(1);
            }}
          >
            <option value="">Todas as perspectivas</option>
            {opts?.perspectives.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Filter>
          <Filter
            id={ids.objective}
            label="Objetivo"
            value={objectiveId}
            onChange={(v) => {
              setObjectiveId(v);
              setPage(1);
            }}
          >
            <option value="">Todos os objetivos</option>
            {objectivesForFilter?.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </Filter>
          <Filter
            id={ids.leader}
            label="Líder"
            value={leaderId}
            onChange={(v) => {
              setLeaderId(v);
              setPage(1);
            }}
          >
            <option value="">Todos os líderes</option>
            {opts?.people.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </Filter>
          <Filter
            id={ids.status}
            label="Status"
            value={status}
            onChange={(v) => {
              setStatus(v as InitiativeStatusName | '');
              setPage(1);
            }}
          >
            <option value="">Todos os status</option>
            {INITIATIVE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {INITIATIVE_STATUS_LABELS[s]}
              </option>
            ))}
          </Filter>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            className="size-4 accent-(--brand)"
            checked={archived}
            onChange={(e) => {
              setArchived(e.target.checked);
              setPage(1);
            }}
          />
          Ver só as arquivadas
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            className="size-4 accent-(--brand)"
            checked={untreatedOnly}
            onChange={(e) => {
              setUntreatedOnly(e.target.checked);
              setPage(1);
            }}
          />
          Só com risco sem tratamento
        </label>
      </div>

      {list.isPending ? (
        <LoadingState rows={5} label="Carregando as iniciativas" />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} retrying={list.isFetching} />
      ) : list.data.total === 0 ? (
        <EmptyState
          className="rounded-lg border border-dashed border-border py-12"
          icon={Rocket}
          title={
            filtered
              ? 'Nenhuma iniciativa encontrada'
              : archived
                ? 'Nenhuma iniciativa arquivada'
                : 'Nenhuma iniciativa ainda'
          }
          description={
            filtered
              ? 'Nenhuma iniciativa corresponde à busca e aos filtros escolhidos.'
              : opts && opts.objectives.length === 0
                ? 'As iniciativas se ligam a um objetivo estratégico: cadastre os objetivos do projeto primeiro.'
                : 'Cadastre as iniciativas que vão levar a empresa aos objetivos: líder, equipe, orçamento, prazo e riscos.'
          }
          action={
            filtered ? (
              <Button variant="outline" onClick={clearFilters}>
                Limpar filtros
              </Button>
            ) : can('iniciativas', 'create') && !archived && opts && opts.objectives.length > 0 ? (
              <Button onClick={() => setEditing('new')}>
                <Plus aria-hidden />
                Nova iniciativa
              </Button>
            ) : null
          }
        />
      ) : (
        <div className="flex flex-col gap-3" aria-busy={list.isFetching}>
          <p className="text-sm text-muted-foreground" aria-live="polite">
            {list.data.total === 1 ? '1 iniciativa' : `${list.data.total} iniciativas`}
          </p>
          <ul aria-label="Iniciativas" className="flex flex-col gap-2">
            {list.data.items.map((i) => (
              <li key={i.id} className="rounded-lg border border-border bg-surface p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold">{i.name}</p>
                      <StatusBadge
                        status={INITIATIVE_STATUS_LIGHT[i.status]}
                        label={INITIATIVE_STATUS_LABELS[i.status]}
                      />
                      {i.late ? <StatusBadge status="red" label="Atrasada" /> : null}
                    </div>
                    <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <Meta
                        icon={Target}
                        label="Objetivo"
                        value={`${i.objective.name}${i.objective.archived ? ' (arquivado)' : ''} · ${i.perspective.name}`}
                      />
                      <Meta icon={UserRound} label="Líder" value={i.leader?.name ?? 'Sem líder'} />
                      <Meta icon={CalendarRange} label="Período" value={period(i)} />
                      <Meta
                        icon={Wallet}
                        label="Orçamento"
                        value={
                          i.budgetPlanned === null
                            ? 'Sem orçamento previsto'
                            : `${formatCurrency(i.budgetActual ?? 0)} de ${formatCurrency(i.budgetPlanned)} (${percent(i.budgetConsumed)})`
                        }
                      />
                      {i.risks.length ? (
                        <Meta
                          icon={ShieldAlert}
                          label="Riscos"
                          value={`${i.risks.length} ${i.risks.length === 1 ? 'risco' : 'riscos'}${riskSummary(i)}`}
                        />
                      ) : null}
                    </dl>
                    <ProgressBar
                      value={i.progress}
                      label={`Progresso de ${i.name}`}
                      className="mt-3 max-w-md"
                    />
                  </div>
                  <div className="flex gap-1 sm:shrink-0">
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Ver detalhes de ${i.name}`}
                      onClick={() => openDetail(i.id)}
                    >
                      <Eye aria-hidden />
                    </Button>
                    {!i.archivedAt && can('iniciativas', 'edit') ? (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Editar ${i.name}`}
                        onClick={() => setEditing(i)}
                        disabled={!opts}
                      >
                        <Pencil aria-hidden />
                      </Button>
                    ) : null}
                    {can('iniciativas', 'delete') ? (
                      i.archivedAt ? (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={toggleArchive.isPending}
                          onClick={() => toggleArchive.mutate(i)}
                        >
                          <ArchiveRestore aria-hidden />
                          Restaurar
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Arquivar ${i.name}`}
                          onClick={() => setArchiving(i)}
                        >
                          <Archive aria-hidden />
                        </Button>
                      )
                    ) : null}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          {list.data.totalPages > 1 ? (
            <nav aria-label="Paginação de iniciativas" className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                Página {list.data.page} de {list.data.totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= list.data.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Próxima
                </Button>
              </div>
            </nav>
          ) : null}
        </div>
      )}

      <InitiativeDetail
        base={base}
        organizationId={organizationId}
        projectId={projectId}
        initiativeId={detailId}
        plansHref={
          canDo('planos-acao', 'view')
            ? (id) => `/empresas/${organizationId}/execucao/planos-acao?iniciativa=${id}`
            : undefined
        }
        onClose={() => openDetail(null)}
        onEdit={
          can('iniciativas', 'edit') && opts
            ? (i) => {
                openDetail(null);
                setEditing(i);
              }
            : undefined
        }
      />
      <Dialog
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        busy={busy}
        title={editing === 'new' ? 'Nova iniciativa' : 'Editar iniciativa'}
        className="w-[min(46rem,calc(100vw-2rem))]"
      >
        {editing !== null && opts ? (
          <InitiativeForm
            initial={editing === 'new' ? undefined : editing}
            options={opts}
            defaultObjectiveId={objectiveId || undefined}
            actionsUrl={editing === 'new' ? undefined : `${base}/${editing.id}/actions`}
            onSubmit={save}
            onCancel={() => setEditing(null)}
            onBusyChange={setBusy}
          />
        ) : null}
      </Dialog>
      <ConfirmDialog
        open={archiving !== null}
        onOpenChange={(open) => !open && setArchiving(null)}
        title="Arquivar a iniciativa?"
        description={`${archiving?.name ?? ''} sai das listas. O histórico, a equipe e os riscos ficam guardados, e você pode restaurá-la depois.`}
        confirmLabel="Arquivar iniciativa"
        destructive
        busy={toggleArchive.isPending}
        onConfirm={() => archiving && toggleArchive.mutate(archiving)}
      />
    </div>
  );
}

/** " (1 crítico, 1 sem tratamento)": riscos de severidade crítica ou alta e os sem ação de tratamento. */
function riskSummary(i: Initiative) {
  const critical = i.risks.filter((k) => k.severity === 'CRITICAL').length;
  const high = i.risks.filter((k) => k.severity === 'HIGH').length;
  const parts = [
    critical ? `${critical} ${critical === 1 ? 'crítico' : 'críticos'}` : '',
    high ? `${high} ${high === 1 ? 'alto' : 'altos'}` : '',
    i.untreatedRisks ? `${i.untreatedRisks} sem tratamento` : '',
  ].filter(Boolean);
  return parts.length ? ` (${parts.join(', ')})` : '';
}

/** Tratamento do risco por extenso: a ação, o status e o plano (ou por que não trata mais). */
function treatmentText(t: InitiativeActionRef | null) {
  if (!t) return 'Sem ação de tratamento';
  const why = t.planArchived ? ' — plano arquivado' : t.status === 'CANCELLED' ? ' — ação cancelada' : '';
  return `Tratamento: ${t.title} (${ACTION_STATUS_LABELS[t.status]}, plano ${t.planName})${why}`;
}

/** Como o progresso foi obtido, para mostrar junto da barra. */
const progressSource = (i: Initiative) =>
  i.progressMode === 'ACTIONS'
    ? i.actions.counted
      ? `Pelas ações: ${i.actions.done} de ${i.actions.counted} concluídas`
      : 'Pelas ações: nenhuma ação nos planos ativos ainda'
    : 'Informado manualmente';

/** Detalhe da iniciativa (aberto pela lista ou por link com ?iniciativa=id). */
function InitiativeDetail({
  base,
  organizationId,
  projectId,
  initiativeId,
  plansHref,
  onClose,
  onEdit,
}: {
  base: string;
  organizationId: string;
  projectId: string;
  initiativeId: string | null;
  /** Atalho para os planos de ação desta iniciativa (quem acessa Planos de Ação). */
  plansHref?: (id: string) => string;
  onClose: () => void;
  onEdit?: (i: Initiative) => void;
}) {
  const { client } = useAuth();
  const detail = useQuery({
    queryKey: ['initiatives', projectId, 'detail', initiativeId],
    queryFn: ({ signal }) => client.request(`${base}/${initiativeId}`, { schema: initiativeSchema, signal }),
    enabled: initiativeId !== null,
  });
  const i = detail.data;
  return (
    <Dialog
      open={initiativeId !== null}
      onOpenChange={(open) => !open && onClose()}
      title={i?.name ?? 'Iniciativa'}
      className="w-[min(46rem,calc(100vw-2rem))]"
    >
      {detail.isPending ? (
        <LoadingState rows={4} label="Carregando a iniciativa" />
      ) : detail.isError ? (
        <ErrorState error={detail.error} onRetry={() => void detail.refetch()} retrying={detail.isFetching} />
      ) : i ? (
        <div className="flex flex-col gap-5 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge
              status={INITIATIVE_STATUS_LIGHT[i.status]}
              label={INITIATIVE_STATUS_LABELS[i.status]}
            />
            {i.late ? <StatusBadge status="red" label="Atrasada" /> : null}
            {i.archivedAt ? <StatusBadge status="gray" label="Arquivada" /> : null}
          </div>
          {i.description ? <p>{i.description}</p> : null}
          <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
            <Fact label="Objetivo relacionado">
              {i.objective.name}
              {i.objective.archived ? ' (arquivado)' : ''}
            </Fact>
            <Fact label="Perspectiva">{i.perspective.name}</Fact>
            <Fact label="Área">{i.department?.name ?? 'Empresa toda'}</Fact>
            <Fact label="Líder">{i.leader?.name ?? 'Sem líder'}</Fact>
            <Fact label="Equipe">{i.team.length ? i.team.map((p) => p.name).join(', ') : 'Sem equipe'}</Fact>
            <Fact label="Período">{period(i)}</Fact>
            <Fact label="Impacto esperado">{i.expectedImpact ?? '—'}</Fact>
            {i.diagnosis ? (
              <Fact label="Origem">
                {i.diagnosis.title === null ? (
                  'Item de diagnóstico (fora do seu acesso)'
                ) : (
                  <Link
                    className="inline-flex items-center underline underline-offset-2"
                    to={`/empresas/${organizationId}/diagnostico?projeto=${projectId}&item=${i.diagnosis.id}`}
                  >
                    Item de diagnóstico: {i.diagnosis.title}
                  </Link>
                )}
              </Fact>
            ) : null}
            <Fact label="Progresso">
              <ProgressBar value={i.progress} label={`Progresso de ${i.name}`} />
              <span className="mt-1 block text-xs font-normal text-muted-foreground">
                {progressSource(i)}
              </span>
            </Fact>
          </dl>
          <section aria-label="Orçamento">
            <h3 className="mb-2 font-bold">Orçamento</h3>
            <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Fact label="Previsto">{formatCurrency(i.budgetPlanned)}</Fact>
              <Fact label="Realizado">{formatCurrency(i.budgetActual)}</Fact>
              <Fact label="Saldo">
                <span className={cn(i.budgetBalance !== null && i.budgetBalance < 0 && 'text-status-red-fg')}>
                  {formatCurrency(i.budgetBalance)}
                </span>
              </Fact>
              <Fact label="Consumido">{percent(i.budgetConsumed)}</Fact>
            </dl>
          </section>
          <section aria-label="Riscos">
            <h3 className="mb-2 font-bold">Riscos</h3>
            {i.risks.length === 0 ? (
              <p className="text-muted-foreground">Nenhum risco cadastrado.</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {i.risks.map((k) => (
                  <li key={k.id} className="rounded-md border border-border p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold">{k.description}</p>
                      <StatusBadge
                        status={SEVERITY_LIGHT[k.severity]}
                        label={`Severidade ${RISK_SEVERITY_LABELS[k.severity].toLowerCase()}`}
                      />
                      {k.untreated ? <StatusBadge status="yellow" label="Sem tratamento" /> : null}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Probabilidade {RISK_PROBABILITY_LABELS[k.probability].toLowerCase()} · impacto{' '}
                      {RISK_IMPACT_LABELS[k.impact].toLowerCase()}
                    </p>
                    <p className="mt-1">
                      {k.response ? `Resposta planejada: ${k.response}` : 'Sem resposta planejada'}
                    </p>
                    <p className="mt-1">
                      {k.treatmentHidden
                        ? 'Tratamento num plano de ação fora do seu acesso'
                        : treatmentText(k.treatment)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </section>
          {i.notes ? (
            <section aria-label="Observações">
              <h3 className="mb-1 font-bold">Observações</h3>
              <p className="whitespace-pre-line">{i.notes}</p>
            </section>
          ) : null}
          <CollaborationPanel
            organizationId={organizationId}
            projectId={projectId}
            subject="initiative"
            subjectId={i.id}
            label={i.name}
          />
          {plansHref || (onEdit && !i.archivedAt) ? (
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              {plansHref ? (
                <Button asChild variant="outline">
                  <Link to={plansHref(i.id)}>
                    <ListChecks aria-hidden />
                    Planos de ação
                  </Link>
                </Button>
              ) : null}
              {onEdit && !i.archivedAt ? (
                <Button variant="outline" onClick={() => onEdit(i)}>
                  <Pencil aria-hidden />
                  Editar iniciativa
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 font-medium break-words">{children}</dd>
    </div>
  );
}

function Filter({
  id,
  label,
  value,
  onChange,
  children,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id} className="sr-only">
        Filtrar por {label.toLowerCase()}
      </label>
      <NativeSelect id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        {children}
      </NativeSelect>
    </div>
  );
}

function Meta({ icon: Icon, label, value }: { icon: typeof Target; label: string; value: string }) {
  return (
    <div className="flex items-center gap-1">
      <Icon className="size-3.5" aria-hidden />
      <dt className="sr-only">{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

const textareaClass =
  'w-full resize-y rounded-md border border-border-strong bg-surface-raised px-3 py-2 text-sm shadow-sm focus-visible:border-brand focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-ring/40 aria-invalid:border-status-red-solid';

/** Erros por campo; os de um risco ficam na linha dele ("risks.0.description"). */
function formErrors(error: Parameters<typeof zodFieldErrors>[0]): FieldErrors {
  const out = zodFieldErrors(error);
  if (error.issues.every((i) => i.path[0] !== 'risks' || i.path.length < 3)) return out;
  delete out.risks;
  for (const issue of error.issues) {
    if (issue.path[0] !== 'risks') continue;
    const key = issue.path.length >= 3 ? `risks.${String(issue.path[1])}.${String(issue.path[2])}` : 'risks';
    out[key] ??= issue.message;
  }
  return out;
}

type RiskRow = {
  key: string;
  id?: string;
  description: string;
  probability: RiskLevelName;
  impact: RiskLevelName;
  response: string;
  treatmentActionId: string;
};

/** Formulário completo da iniciativa: campos do §11.18, equipe e riscos. */
/**
 * Cadastro da iniciativa. Também usado para transformar um item de diagnóstico em iniciativa (T-160b; DEC-061):
 * `prefill` traz o título, a descrição e o objetivo relacionado do item.
 */
export function InitiativeForm({
  initial,
  options,
  defaultObjectiveId,
  prefill,
  submitLabel,
  actionsUrl,
  onSubmit,
  onCancel,
  onBusyChange,
}: {
  initial?: Initiative;
  options: InitiativeOptions;
  defaultObjectiveId?: string;
  /** Campos já preenchidos de uma iniciativa nova (a partir de um item de diagnóstico). */
  prefill?: { name: string; description: string | null; objectiveId: string | null };
  /** Texto do botão de salvar de uma iniciativa nova (padrão: "Criar iniciativa"). */
  submitLabel?: string;
  /** Ações dos planos da iniciativa (para o tratamento dos riscos); só ao editar. */
  actionsUrl?: string;
  onSubmit: (input: InitiativeInput) => Promise<void>;
  onCancel: () => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const { client } = useAuth();
  const planActions = useQuery({
    queryKey: ['initiatives', 'actions', actionsUrl],
    queryFn: ({ signal }) => client.request(actionsUrl!, { schema: initiativeActionsSchema, signal }),
    enabled: Boolean(actionsUrl),
    // Os planos mudam em outra tela: ao abrir o formulário, as ações vêm sempre atualizadas.
    refetchOnMount: 'always',
  });
  const [name, setName] = useState(initial?.name ?? prefill?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? prefill?.description ?? '');
  const [objectiveId, setObjectiveId] = useState(
    initial?.objective.id ??
      (prefill?.objectiveId && options.objectives.some((o) => o.id === prefill.objectiveId)
        ? prefill.objectiveId
        : undefined) ??
      defaultObjectiveId ??
      options.objectives[0]?.id ??
      '',
  );
  const [leaderId, setLeaderId] = useState(initial?.leader?.id ?? '');
  const [team, setTeam] = useState<string[]>(initial?.team.map((p) => p.id) ?? []);
  const [status, setStatus] = useState<InitiativeStatusName>(initial?.status ?? 'PLANNED');
  const [progress, setProgress] = useState(initial ? String(initial.manualProgress) : '0');
  const [progressMode, setProgressMode] = useState<InitiativeProgressModeName>(
    initial?.progressMode ?? 'MANUAL',
  );
  const [startDate, setStartDate] = useState(initial?.startDate ?? '');
  const [endDate, setEndDate] = useState(initial?.endDate ?? '');
  const [budgetPlanned, setBudgetPlanned] = useState(moneyText(initial?.budgetPlanned));
  const [budgetActual, setBudgetActual] = useState(moneyText(initial?.budgetActual));
  const [expectedImpact, setExpectedImpact] = useState(initial?.expectedImpact ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [risks, setRisks] = useState<RiskRow[]>(
    initial?.risks.map((k) => ({
      key: k.id,
      id: k.id,
      description: k.description,
      probability: k.probability,
      impact: k.impact,
      response: k.response ?? '',
      treatmentActionId: k.treatment?.id ?? '',
    })) ?? [],
  );
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const ids = {
    name: useId(),
    description: useId(),
    objective: useId(),
    leader: useId(),
    team: useId(),
    status: useId(),
    progress: useId(),
    progressMode: useId(),
    start: useId(),
    end: useId(),
    planned: useId(),
    actual: useId(),
    impact: useId(),
    notes: useId(),
    risks: useId(),
  };

  // Pessoa ou objetivo atual que saiu das opções ativas continua escolhível (salvar não troca).
  const people = [
    ...options.people,
    ...[initial?.leader, ...(initial?.team ?? [])].filter(
      (p, idx, all): p is { id: string; name: string } =>
        !!p && !options.people.some((o) => o.id === p.id) && all.findIndex((x) => x?.id === p.id) === idx,
    ),
  ];
  const keptObjective =
    initial && !options.objectives.some((o) => o.id === initial.objective.id) ? initial.objective : null;
  const perspectiveName = (id: string) => options.perspectives.find((p) => p.id === id)?.name ?? 'Outra';
  const objectiveGroups = options.perspectives
    .map((p) => ({ perspective: p, objectives: options.objectives.filter((o) => o.perspectiveId === p.id) }))
    .filter((g) => g.objectives.length);
  const orphan = options.objectives.filter(
    (o) => !options.perspectives.some((p) => p.id === o.perspectiveId),
  );

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setFormError(null);
    const parsed = initiativeInputSchema.safeParse({
      name,
      description,
      objectiveId: objectiveId || undefined,
      leaderId: leaderId || null,
      team,
      status,
      // Pelas ações, o campo de % some: vai o valor informado que estava guardado (não o digitado antes).
      progress:
        progressMode === 'ACTIONS'
          ? (initial?.manualProgress ?? 0)
          : progress.trim() === ''
            ? 0
            : progress.replace(',', '.'),
      progressMode,
      startDate: startDate || null,
      endDate: endDate || null,
      budgetPlanned: moneyInput(budgetPlanned),
      budgetActual: moneyInput(budgetActual),
      expectedImpact,
      notes,
      risks: risks.map((k) => ({
        ...(k.id ? { id: k.id } : {}),
        description: k.description,
        probability: k.probability,
        impact: k.impact,
        response: k.response,
        treatmentActionId: k.treatmentActionId || null,
      })),
    });
    if (!parsed.success) return setErrors(formErrors(parsed.error));
    setErrors({});
    setSaving(true);
    onBusyChange(true);
    try {
      // A iniciativa vai inteira (PUT substitui equipe e riscos): campo esvaziado vai como null.
      await onSubmit(parsed.data);
    } catch (err) {
      const fields = apiFieldErrors(err);
      // Erro de um risco (ex.: ação de tratamento) vai para a linha dele.
      if (err instanceof ApiError && err.details.some((d) => d.path.startsWith('risks.'))) {
        delete fields.risks;
        for (const d of err.details) if (d.path.startsWith('risks.')) fields[d.path] ??= d.message;
      }
      if (hasFieldErrors(fields)) setErrors(fields);
      else setFormError(err);
    } finally {
      setSaving(false);
      onBusyChange(false);
    }
  }

  /** O risco tem tratamento num plano fora do acesso de quem edita (não aparece; "sem escolha" o mantém). */
  const hiddenTreatment = (riskId: string | undefined) =>
    Boolean(initial?.risks.find((x) => x.id === riskId)?.treatmentHidden);
  // Ações que podem tratar um risco: as dos planos ativos que valem; a já vinculada continua escolhível.
  const treatmentOptions = (current: string) => {
    const items = planActions.data?.items ?? [];
    const kept = initial?.risks.find((k) => k.treatment?.id === current)?.treatment;
    return [
      ...items.filter((a) => a.status !== 'CANCELLED' || a.id === current),
      ...(kept && !items.some((a) => a.id === kept.id) ? [kept] : []),
    ];
  };

  const riskError = (i: number, field: string) => errors[`risks.${i}.${field}`];

  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-5">
      <FormField id={ids.name} label="Nome" error={errors.name}>
        <Input
          {...fieldA11y(ids.name, errors.name)}
          value={name}
          maxLength={160}
          placeholder="Ex.: Programa de expansão comercial"
          onChange={(e) => setName(e.target.value)}
          className="h-11"
        />
      </FormField>
      <FormField id={ids.description} label="Descrição (opcional)" error={errors.description}>
        <textarea
          {...fieldA11y(ids.description, errors.description)}
          value={description}
          rows={2}
          maxLength={2000}
          onChange={(e) => setDescription(e.target.value)}
          className={textareaClass}
        />
      </FormField>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField id={ids.objective} label="Objetivo relacionado" error={errors.objectiveId}>
          <NativeSelect
            {...fieldA11y(ids.objective, errors.objectiveId)}
            value={objectiveId}
            onChange={(e) => setObjectiveId(e.target.value)}
          >
            <option value="">Escolha o objetivo</option>
            {keptObjective ? (
              <option value={keptObjective.id}>
                {keptObjective.name}
                {keptObjective.archived ? ' (arquivado)' : ''}
              </option>
            ) : null}
            {objectiveGroups.map((g) => (
              <optgroup key={g.perspective.id} label={g.perspective.name}>
                {g.objectives.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </optgroup>
            ))}
            {orphan.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name} · {perspectiveName(o.perspectiveId)}
              </option>
            ))}
          </NativeSelect>
        </FormField>
        <FormField id={ids.status} label="Status" error={errors.status}>
          <NativeSelect
            {...fieldA11y(ids.status, errors.status)}
            value={status}
            onChange={(e) => setStatus(e.target.value as InitiativeStatusName)}
          >
            {INITIATIVE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {INITIATIVE_STATUS_LABELS[s]}
              </option>
            ))}
          </NativeSelect>
        </FormField>
        <FormField id={ids.leader} label="Líder" error={errors.leaderId}>
          <NativeSelect
            {...fieldA11y(ids.leader, errors.leaderId)}
            value={leaderId}
            onChange={(e) => setLeaderId(e.target.value)}
          >
            <option value="">Sem líder</option>
            {people.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </NativeSelect>
        </FormField>
        <FormField id={ids.progressMode} label="Como medir o progresso" error={errors.progressMode}>
          <NativeSelect
            {...fieldA11y(ids.progressMode, errors.progressMode)}
            value={progressMode}
            onChange={(e) => setProgressMode(e.target.value as InitiativeProgressModeName)}
          >
            {INITIATIVE_PROGRESS_MODES.map((m) => (
              <option key={m} value={m}>
                {INITIATIVE_PROGRESS_MODE_LABELS[m]}
              </option>
            ))}
          </NativeSelect>
        </FormField>
        {progressMode === 'MANUAL' ? (
          <FormField id={ids.progress} label="Progresso (%)" error={errors.progress}>
            <Input
              {...fieldA11y(ids.progress, errors.progress)}
              value={progress}
              inputMode="decimal"
              maxLength={6}
              onChange={(e) => setProgress(e.target.value)}
              className="h-11"
            />
          </FormField>
        ) : (
          <div className="self-end pb-3 text-sm">
            <p className="font-medium">
              {initial?.progressMode === 'ACTIONS'
                ? `Progresso atual: ${formatDecimal(initial.progress)}%`
                : 'Calculado ao salvar'}
            </p>
            <p className="text-xs text-muted-foreground">
              Concluídas sobre as ações que valem (não canceladas) dos planos ativos desta iniciativa.
            </p>
          </div>
        )}
        <FormField id={ids.start} label="Data inicial" error={errors.startDate}>
          <Input
            {...fieldA11y(ids.start, errors.startDate)}
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="h-11"
          />
        </FormField>
        <FormField id={ids.end} label="Data final" error={errors.endDate}>
          <Input
            {...fieldA11y(ids.end, errors.endDate)}
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="h-11"
          />
        </FormField>
        <FormField id={ids.planned} label="Orçamento previsto (R$)" error={errors.budgetPlanned}>
          <Input
            {...fieldA11y(ids.planned, errors.budgetPlanned)}
            value={budgetPlanned}
            inputMode="decimal"
            maxLength={20}
            placeholder="Ex.: 250.000,00"
            onChange={(e) => setBudgetPlanned(e.target.value)}
            className="h-11"
          />
        </FormField>
        <FormField id={ids.actual} label="Orçamento realizado (R$)" error={errors.budgetActual}>
          <Input
            {...fieldA11y(ids.actual, errors.budgetActual)}
            value={budgetActual}
            inputMode="decimal"
            maxLength={20}
            onChange={(e) => setBudgetActual(e.target.value)}
            className="h-11"
          />
        </FormField>
      </div>

      <fieldset>
        <legend className="text-sm font-medium">Equipe</legend>
        <div
          id={ids.team}
          className="mt-2 grid max-h-44 gap-1 overflow-y-auto rounded-md border border-border p-2 sm:grid-cols-2"
        >
          {people.map((u) => (
            <label
              key={u.id}
              className="flex min-h-11 items-center gap-2 rounded px-2 text-sm hover:bg-muted"
            >
              <input
                type="checkbox"
                className="size-4 accent-(--brand)"
                checked={team.includes(u.id)}
                onChange={(e) =>
                  setTeam((all) => (e.target.checked ? [...all, u.id] : all.filter((x) => x !== u.id)))
                }
              />
              {u.name}
            </label>
          ))}
        </div>
        {errors.team ? <p className="mt-1.5 text-sm font-medium text-status-red-fg">{errors.team}</p> : null}
      </fieldset>

      <FormField id={ids.impact} label="Impacto esperado (opcional)" error={errors.expectedImpact}>
        <textarea
          {...fieldA11y(ids.impact, errors.expectedImpact)}
          value={expectedImpact}
          rows={2}
          maxLength={2000}
          placeholder="Ex.: receita 15% maior na regional"
          onChange={(e) => setExpectedImpact(e.target.value)}
          className={textareaClass}
        />
      </FormField>

      <fieldset aria-describedby={`${ids.risks}-dica`}>
        <legend className="text-sm font-medium">Riscos</legend>
        <p id={`${ids.risks}-dica`} className="mt-1 text-xs text-muted-foreground">
          O que pode atrapalhar a iniciativa, com a probabilidade, o impacto e a resposta planejada.
        </p>
        {risks.length ? (
          <ul className="mt-2 flex flex-col gap-3">
            {risks.map((k, i) => (
              <li key={k.key} className="rounded-md border border-border p-3">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Input
                      aria-label={`Descrição do risco ${i + 1}`}
                      aria-invalid={riskError(i, 'description') ? true : undefined}
                      value={k.description}
                      maxLength={500}
                      placeholder="Ex.: atraso na contratação da equipe"
                      onChange={(e) =>
                        setRisks((all) =>
                          all.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)),
                        )
                      }
                      className="h-10"
                    />
                    {riskError(i, 'description') ? (
                      <p className="mt-1 text-sm font-medium text-status-red-fg">
                        {riskError(i, 'description')}
                      </p>
                    ) : null}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Remover risco ${i + 1}`}
                    onClick={() => setRisks((all) => all.filter((_, j) => j !== i))}
                  >
                    <Trash2 aria-hidden />
                  </Button>
                </div>
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  <NativeSelect
                    aria-label={`Probabilidade do risco ${i + 1}`}
                    value={k.probability}
                    onChange={(e) =>
                      setRisks((all) =>
                        all.map((x, j) =>
                          j === i ? { ...x, probability: e.target.value as RiskLevelName } : x,
                        ),
                      )
                    }
                  >
                    {RISK_LEVELS.map((l) => (
                      <option key={l} value={l}>
                        Probabilidade {RISK_PROBABILITY_LABELS[l].toLowerCase()}
                      </option>
                    ))}
                  </NativeSelect>
                  <NativeSelect
                    aria-label={`Impacto do risco ${i + 1}`}
                    value={k.impact}
                    onChange={(e) =>
                      setRisks((all) =>
                        all.map((x, j) => (j === i ? { ...x, impact: e.target.value as RiskLevelName } : x)),
                      )
                    }
                  >
                    {RISK_LEVELS.map((l) => (
                      <option key={l} value={l}>
                        Impacto {RISK_IMPACT_LABELS[l].toLowerCase()}
                      </option>
                    ))}
                  </NativeSelect>
                </div>
                <Input
                  aria-label={`Resposta planejada ao risco ${i + 1}`}
                  value={k.response}
                  maxLength={1000}
                  placeholder="Resposta planejada (opcional): mitigar, aceitar, transferir…"
                  onChange={(e) =>
                    setRisks((all) => all.map((x, j) => (j === i ? { ...x, response: e.target.value } : x)))
                  }
                  className="mt-2 h-10"
                />
                {initial ? (
                  <div className="mt-2">
                    <NativeSelect
                      aria-label={`Ação de tratamento do risco ${i + 1}`}
                      aria-invalid={riskError(i, 'treatmentActionId') ? true : undefined}
                      value={k.treatmentActionId}
                      disabled={planActions.isPending}
                      onChange={(e) =>
                        setRisks((all) =>
                          all.map((x, j) => (j === i ? { ...x, treatmentActionId: e.target.value } : x)),
                        )
                      }
                    >
                      <option value="">
                        {planActions.isPending
                          ? 'Carregando as ações dos planos…'
                          : hiddenTreatment(k.id)
                            ? 'Mantém o atual (plano fora do seu acesso)'
                            : 'Sem ação de tratamento'}
                      </option>
                      {treatmentOptions(k.treatmentActionId).map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.title} · {a.planName}
                          {a.status === 'CANCELLED'
                            ? ' (cancelada)'
                            : a.planArchived
                              ? ' (plano arquivado)'
                              : ''}
                        </option>
                      ))}
                    </NativeSelect>
                    {riskError(i, 'treatmentActionId') ? (
                      <p className="mt-1 text-sm font-medium text-status-red-fg">
                        {riskError(i, 'treatmentActionId')}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
        {initial ? (
          planActions.isError ? (
            <p className="mt-2 text-xs text-status-red-fg">
              Não foi possível carregar as ações dos planos: {errorMessage(planActions.error)}
            </p>
          ) : planActions.data && planActions.data.items.length === 0 ? (
            <p className="mt-2 text-xs text-muted-foreground">
              O tratamento de um risco é uma ação de um plano desta iniciativa: crie o plano em Planos de
              ação.
            </p>
          ) : null
        ) : (
          <p className="mt-2 text-xs text-muted-foreground">
            Depois de criar a iniciativa, crie os planos de ação dela para vincular a ação que trata cada
            risco.
          </p>
        )}
        {errors.risks ? (
          <p className="mt-1.5 text-sm font-medium text-status-red-fg">{errors.risks}</p>
        ) : null}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-2"
          disabled={risks.length >= 50}
          onClick={() =>
            setRisks((all) => [
              ...all,
              {
                key: `novo-${Date.now()}-${all.length}`,
                description: '',
                probability: 'MEDIUM',
                impact: 'MEDIUM',
                response: '',
                treatmentActionId: '',
              },
            ])
          }
        >
          <ShieldAlert aria-hidden />
          Adicionar risco
        </Button>
      </fieldset>

      <FormField id={ids.notes} label="Observações (opcional)" error={errors.notes}>
        <textarea
          {...fieldA11y(ids.notes, errors.notes)}
          value={notes}
          rows={2}
          maxLength={2000}
          onChange={(e) => setNotes(e.target.value)}
          className={textareaClass}
        />
      </FormField>
      {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" loading={saving}>
          {initial ? 'Salvar iniciativa' : (submitLabel ?? 'Criar iniciativa')}
        </Button>
      </div>
    </form>
  );
}
